import { formatMinorUnitsAsCurrency } from '@shared/utils/currency/formatMinorUnitsAsCurrency';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';
import type { TravelHoneymoonConfig } from './useTravelHoneymoonConfig.types';

const USD = 'USD';
const USD_MINOR_UNITS_PER_MAJOR = 100;

type HoneymoonPromoNumbers = Readonly<{
  /**
   * The headline saving for the promo sentence, always in US dollars — `"US$100"` on a locale that
   * distinguishes the symbol, `"$100"` on one that does not, or undefined.
   *
   * One figure for every fund currency for now, so a GBP couple sees the same figure as a USD one.
   *
   * Formatted exactly as the search page's own fund and coupon lines are — same helper, same locale,
   * same options — because a couple reads the promo and the results it leads to in one sitting, and
   * `USD 100` beside `US$144` looks like two different products. Disambiguation is left to the
   * locale, which is where it belongs: en-GB and en-IN draw USD as `US$` on their own.
   */
  maxSavingsDisplay?: string;
  /**
   * Best-case percentage for the promo slider's "up to X% off" badge, in any currency. Its own flag
   * field: the badge advertises the best stay on Joy, the sentence promises this couple's own
   * saving, and the two are tuned separately.
   *
   * Never the dollar amount — that figure is a whole-trip saving, so on a per-night tile it would
   * promise far more than the stay gives.
   */
  promoSliderPercentOff?: number;
}>;

/**
 * Derives both promo numbers from an already-read config.
 *
 * Pure, so a surface that reads the flag for something else (the search URL's honeymoon location)
 * reuses that one read. `useIsFeatureEnabled` fires an Amplitude exposure on every evaluation and
 * the exposure queue does not dedupe, so a second read would double-count the same view.
 *
 * `undefined` is a real answer, not a failure — the caller then uses its plain sentence and shows no
 * badge. There is deliberately no default figure: an invented saving is a promise we would have to
 * keep at checkout.
 */
export const getHoneymoonPromoNumbers = (config: TravelHoneymoonConfig): HoneymoonPromoNumbers => {
  const amountUsdMajorUnits = config.maxDiscountAmountUsdMajorUnits;

  return {
    // Formatted through the shared money helper so it groups and places its symbol like every other
    // amount on the page, and drops the ".00" a round figure would otherwise carry.
    //
    // Deliberately the same options as FundCoverageStrip's coupon line on the search page. Do not
    // add `useCurrencyCode` back here without changing that line too, or the promo and the results
    // it leads to disagree about how one currency is written.
    maxSavingsDisplay: amountUsdMajorUnits
      ? formatMinorUnitsAsCurrency(amountUsdMajorUnits * USD_MINOR_UNITS_PER_MAJOR, USD, readNavigatorLanguage(), { hideWholeUnitCents: true })
      : undefined,
    promoSliderPercentOff: config.promoSliderMaxDiscountPercentage,
  };
};
